import React, { useState } from "react";
import CustomModal from "../../components/CustomModal";
import { useRegisterUser, useGetAllPartners } from "./hooks";
import NewPartnerModal from "./NewPartnerModal";
import { CONTENT_SECTIONS } from "../../constants/contentSections";
import { useT } from "../../i18n";

interface NewUserModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const userRoles = ["superuser", "user"];
const accountTypes = ["admin", "marketer", "partner"];
const adminRoles = ["admin", "superadmin"];

const NewUserModal: React.FC<NewUserModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const t = useT();
  const [username, setUsername] = useState("");
  const [userRole, setUserRole] = useState("user");
  const [accountType, setAccountType] = useState("admin");
  const [adminRole, setAdminRole] = useState("admin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [partnerCompany, setPartnerCompany] = useState("");
  const [contentAccess, setContentAccess] = useState<string[]>([]);
  const [error, setError] = useState("");

  const registerUserMutation = useRegisterUser();
  const {
    data: partnersResponse,
    isLoading: partnersLoading,
    refetch: refetchPartners,
  } = useGetAllPartners();

  const [newPartnerModalOpen, setNewPartnerModalOpen] = useState(false);

  const partners = partnersResponse?.data || [];

  const resetForm = () => {
    setUsername("");
    setUserRole("user");
    setAccountType("admin");
    setAdminRole("admin");
    setEmail("");
    setPassword("");
    setPartnerCompany("");
    setContentAccess([]);
    setError("");
  };

  const toggleSection = (key: string) =>
    setContentAccess((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const handleSave = async () => {
    setError("");

    // Basic validation for all users
    if (!username || !accountType || !email || !password) {
      setError(t("common.validation.requiredFields"));
      return;
    }

    // Admin-specific validation
    if (accountType === "admin" && !adminRole) {
      setError(t("users.newUserModal.errors.adminRoleRequiredSelect"));
      return;
    }

    // Validation based on account type
    if (accountType === "partner") {
      if (!userRole) {
        setError(t("users.newUserModal.errors.userRoleRequired"));
        return;
      }
      if (!partnerCompany) {
        setError(t("users.newUserModal.errors.partnerCompanyRequired"));
        return;
      }
    } else if (accountType === "admin") {
      if (!adminRole) {
        setError(t("users.newUserModal.errors.adminRoleRequired"));
        return;
      }
    } else if (accountType === "marketer") {
      if (contentAccess.length === 0) {
        setError(t("users.newUserModal.errors.contentSectionRequired"));
        return;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t("common.validation.invalidEmail"));
      return;
    }

    // Password validation
    if (password.length < 6) {
      setError(t("common.validation.passwordMinLength"));
      return;
    }

    try {
      const payload: any = {
        username,
        accountType,
        email,
        password,
      };

      // Add specific fields based on account type
      if (accountType === "partner") {
        payload.userRole = userRole;
        payload.partnerCompany = partnerCompany;
      } else if (accountType === "admin") {
        payload.userRole = "admin";
        payload.adminRole = adminRole;
      } else if (accountType === "marketer") {
        payload.userRole = "marketer";
        payload.contentAccess = contentAccess;
      }

      await registerUserMutation.mutateAsync(payload);

      resetForm();
      onClose();
      onSuccess?.();
    } catch (error: any) {
      setError(t("users.newUserModal.errors.createFailed"));
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handlePartnerCreated = (createdPartner: any) => {
    // createdPartner can be response.data or the object itself
    const createdUuid = createdPartner?.data?.uuid || createdPartner?.uuid;
    if (createdUuid) {
      setPartnerCompany(createdUuid);
    }
    // refetch partners list
    try {
      refetchPartners();
    } catch (err) {
      // ignore
    }
  };

  return (
    <CustomModal open={open} onClose={onClose}>
      <div
        className="w-[820px] max-w-full h-full flex flex-col mx-auto"
        style={{ height: "100%" }}
      >
        <div className="flex flex-col w-full h-full" style={{ height: "100%" }}>
          <div className="flex items-center gap-3 pt-8 pb-2 border-b border-gray-200 w-full pl-0 pr-0">
            <div
              className="flex items-center justify-center w-10 h-10 rounded-lg ml-0"
              style={{ backgroundColor: "#7E3AF220" }}
            >
              <i
                className="fa-solid fa-user-plus text-2xl"
                style={{ color: "#7E3AF2" }}
              ></i>
            </div>
            <div className="ml-0">
              <h2 className="text-xl font-semibold text-gray-900">
                {t("users.newUserModal.title")}
              </h2>
              <div className="text-sm text-gray-400 font-medium leading-tight">
                {t("users.newUserModal.subtitle")}
              </div>
            </div>
          </div>
          <form
            className="py-8 space-y-5 w-full flex flex-col justify-center px-0 flex-1"
            style={{ height: "100%" }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.newUserModal.accountTypeLabel")} <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={accountType}
                onChange={(e) => {
                  setAccountType(e.target.value);
                  // Reset form when changing account type
                  setUserRole("");
                  setPartnerCompany("");
                  setAdminRole("");
                  setContentAccess([]);
                  setEmail("");
                  setPassword("");
                }}
                required
              >
                <option value="">{t("users.newUserModal.accountTypePlaceholder")}</option>
                {accountTypes.map((type) => (
                  <option key={type} value={type}>
                    {t(`users.accountTypes.${type}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Partner Company field - Show first for partner accounts */}
            {accountType === "partner" && (
              <div className="px-0">
                <label className="block text-sm text-gray-700 mb-1 ml-0">
                  {t("users.newUserModal.partnerCompanyLabel")} <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={partnerCompany}
                  onChange={(e) => setPartnerCompany(e.target.value)}
                  required
                  disabled={partnersLoading}
                >
                  <option value="">
                    {partnersLoading
                      ? t("users.newUserModal.partnersLoading")
                      : t("users.newUserModal.partnerCompanyPlaceholder")}
                  </option>
                  {partners.map((partner: any) => (
                    <option key={partner.uuid} value={partner.uuid}>
                      {partner.companyName}
                    </option>
                  ))}
                </select>

                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    className="text-sm text-blue-600 hover:underline"
                    onClick={() => setNewPartnerModalOpen(true)}
                  >
                    {t("users.newUserModal.addCompany")}
                  </button>
                </div>
              </div>
            )}

            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.newUserModal.usernameLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t("users.newUserModal.usernamePlaceholder")}
                required
              />
            </div>

            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.newUserModal.emailLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("users.newUserModal.emailPlaceholder")}
                required
              />
            </div>

            {/* Admin Role field - Only show for admin accounts */}
            {accountType === "admin" && (
              <div className="px-0">
                <label className="block text-sm text-gray-700 mb-1 ml-0">
                  {t("users.newUserModal.adminRoleLabel")} <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={adminRole}
                  onChange={(e) => setAdminRole(e.target.value)}
                  required
                >
                  <option value="">{t("users.newUserModal.adminRolePlaceholder")}</option>
                  <option value="admin">{t("users.adminRoles.admin")}</option>
                  <option value="superadmin">{t("users.adminRoles.superadmin")}</option>
                </select>
              </div>
            )}

            {/* Content sections - Only show for marketer accounts */}
            {accountType === "marketer" && (
              <div className="px-0">
                <label className="block text-sm text-gray-700 mb-1 ml-0">
                  {t("users.newUserModal.contentAccessLabel")} <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-400 mb-2">
                  {t("users.newUserModal.contentAccessHint")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {CONTENT_SECTIONS.map((section) => {
                    const selected = contentAccess.includes(section.key);
                    return (
                      <button
                        key={section.key}
                        type="button"
                        onClick={() => toggleSection(section.key)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${
                          selected
                            ? "border-purple-400 bg-purple-50 text-purple-800"
                            : "border-gray-300 bg-white text-gray-600 hover:border-purple-300"
                        }`}
                      >
                        <i
                          className={`fa-solid ${
                            selected ? "fa-square-check" : "fa-square"
                          } ${selected ? "text-purple-600" : "text-gray-300"}`}
                        ></i>
                        <i className={`fa-solid ${section.icon} w-4 text-center`}></i>
                        {t(`contentSections.${section.key}`)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* User Role field - Only show for partner accounts */}
            {accountType === "partner" && (
              <div className="px-0">
                <label className="block text-sm text-gray-700 mb-1 ml-0">
                  {t("users.newUserModal.userRoleLabel")} <span className="text-red-500">*</span>
                </label>
                <select
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200"
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  required
                >
                  <option value="">{t("users.newUserModal.userRolePlaceholder")}</option>
                  {userRoles.map((role) => (
                    <option key={role} value={role}>
                      {t(`users.userRoles.${role}`)}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="px-0">
              <label className="block text-sm text-gray-700 mb-1 ml-0">
                {t("users.newUserModal.passwordLabel")} <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                className="w-full border border-gray-300 rounded-lg px-4 py-2 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t("users.newUserModal.passwordPlaceholder")}
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm font-medium mt-2 px-0">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2 px-0">
              <button
                type="button"
                className="px-6 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 font-medium"
                onClick={handleClose}
                disabled={registerUserMutation.isPending}
              >
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="px-6 py-2 rounded-lg text-white hover:opacity-90 font-medium flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: "#7E3AF2" }}
                disabled={registerUserMutation.isPending}
              >
                {registerUserMutation.isPending ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin"></i>
                    {t("common.loading.creating")}
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-floppy-disk"></i>
                    {t("users.newUserModal.submit")}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      <NewPartnerModal
        open={newPartnerModalOpen}
        onClose={() => setNewPartnerModalOpen(false)}
        onSuccess={handlePartnerCreated}
      />
    </CustomModal>
  );
};

export default NewUserModal;
